import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMount } from 'react-use';

import {
  Content,
  Header,
  Heading,
  Separator,
  SidebarTrigger,
  Stack,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import {
  DynamicArtifactTemplateBuilder,
  templateBuilderStore,
} from '@eluve/smart-blocks';

import { DynamicArtifactTemplatePreview } from './components/DynamicArtifactPreview';
import { PrefillTemplateSection } from './components/PrefillTemplateSection';
import { TemplateEditingPageHeaderButtons } from './components/TemplateEditingPageHeaderButtons';
import { useCreateTemplate } from './useCreateTemplate';
import { useIsOrganizationRoute } from './useIsOrganizationRoute';

export const CreateOutputTemplatePage: React.FC = () => {
  const { t } = useTranslation();
  const [isValid, setIsValid] = useState(false);
  const navigate = useNavigate();
  const isOrganizationRoute = useIsOrganizationRoute();

  const { handleSaveDraftTemplate, handleSubmitTemplate } = useCreateTemplate();

  const handleIsValidChange = useCallback((valid: boolean) => {
    setIsValid(valid);
  }, []);

  useMount(() => {
    templateBuilderStore.send({ type: 'reset' });
  });

  return (
    <>
      <Header>
        {isOrganizationRoute && <SidebarTrigger />}
        <div className="my-2 w-full py-2">
          <Heading>{t('outputTemplates.common.createTemplate')}</Heading>
        </div>

        <TemplateEditingPageHeaderButtons
          handleSaveDraft={handleSaveDraftTemplate}
          handleSubmit={handleSubmitTemplate}
          handleDeleteDraft={() => navigate('..')}
          saveDraftDisabled={!isValid}
          submitDisabled={!isValid}
        />
      </Header>

      <Content>
        <Stack
          align="stretch"
          className="divide-x"
          gap={2}
          direction="horizontal"
          fullHeight
        >
          <Stack
            className="min-w-0 flex-1 overflow-y-auto p-6"
            gap={6}
            fullHeight
          >
            <PrefillTemplateSection />
            <Separator />
            <DynamicArtifactTemplateBuilder
              onIsValidChange={handleIsValidChange}
              showJsonSchema
            />
          </Stack>
          <Stack
            className="min-w-0 flex-1 overflow-y-auto bg-bg-tertiary p-6"
            gap={2}
            fullHeight
          >
            <DynamicArtifactTemplatePreview />
          </Stack>
        </Stack>
      </Content>
    </>
  );
};
